import * as React from "react";
import classNames from "../utils/classnames";
import { Navigation } from "./Navigation";
import { PageOptions } from "./PageOptions";
import { Paper } from "./Paper";
import { useHtmlTitle } from "./useHtmlTitle";

export namespace Page {
  export type Size = "tiny" | "small" | "medium" | "large" | "xlarge" | "xxlarge" | "fullwidth";

  export interface Option {
    type: "link" | "action";
    icon: React.ElementType;
    label: string;
    link?: string;
    onClick?: () => void;
    hidden?: boolean;
    testId?: string;
    keepOutsideOnBigScreen?: boolean;
    keepOutside?: boolean;
    iconOnly?: boolean;
  }

  export interface Props {
    title: string | string[];
    size?: Size;
    options?: Option[];
    children?: React.ReactNode;
    navigation?: Navigation.Item[];
    navigationTestId?: string;
    optionsTestId?: string;
    testId?: string;
    className?: string;
  }
}

const sizeClasses: Record<Page.Size, string> = {
  tiny: "max-w-xl mx-auto",
  small: "max-w-2xl mx-auto",
  medium: "max-w-4xl mx-auto",
  large: "sm:max-w-[90%] lg:max-w-5xl mx-auto",
  xlarge: "sm:max-w-[90%] lg:max-w-6xl mx-auto",
  xxlarge: "sm:max-w-[90%] lg:max-w-7xl mx-auto",
  fullwidth: "max-w-full",
};

// Old style pages
export function Page(props: Page.Props) {
  useHtmlTitle(props.title);
  const containerClass = classNames("sm:my-4", sizeClasses[props.size || "medium"]);

  return (
    <div className={containerClass}>
      {props.navigation && <Navigation items={props.navigation} testId={props.navigationTestId ?? "navigation"} />}

      <Paper testId={props.testId}>
        <PageOptions options={props.options} testId={props.optionsTestId ?? "options-button"} />
        {props.children}
      </Paper>
    </div>
  );
}

// Constrained width, no paper card (e.g. Home)
export function PageOpen(props: Page.Props) {
  useHtmlTitle(props.title);

  const containerClass = classNames("relative sm:my-10", sizeClasses[props.size || "medium"], props.className);

  return (
    <div className={containerClass} data-test-id={props.testId}>
      {props.navigation && <Navigation items={props.navigation} testId={props.navigationTestId ?? "navigation"} />}
      {props.options && <PageOptions options={props.options} testId={props.optionsTestId ?? "options-button"} />}
      {props.children}
    </div>
  );
}

// New style pages
export function PageNew(props: Page.Props & { fillHeight?: boolean }) {
  useHtmlTitle(props.title);

  const containerClass = classNames("absolute inset-0", props.fillHeight && "flex flex-col");
  const innerClass = classNames(
    "bg-surface-base flex flex-col",
    props.fillHeight ? "flex-1 min-h-0 overflow-hidden" : "min-h-full",
    props.className,
  );
  const contentClass = classNames(
    sizeClasses[props.size || "medium"],
    props.fillHeight && "flex flex-col flex-1 min-h-0",
  );

  return (
    <div className={containerClass} data-test-id={props.testId}>
      {props.navigation && <Navigation items={props.navigation} testId={props.navigationTestId ?? "navigation"} />}

      <div className={innerClass}>
        <PageOptions options={props.options} testId={props.optionsTestId ?? "options-button"} />
        <div className={contentClass}>{props.children}</div>
      </div>
    </div>
  );
}
